import { Component, computed, effect, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { $state } from 'app/ajs-upgraded-providers';
import { delay, of } from 'rxjs';

import {
  ConfigurationManagerService,
  EntityManagerService,
  LoaderService,
  ScreenOrientationService,
  ToolbarEventsService,
  UserActiveSessionManagerService
} from 'src/app/core';

import { GridCellFormatterService, GridClientSideComponent, GridFixedButtons, headersTranslate } from 'src/app/shared/components';
import { DataExport, TableData } from 'src/app/shared/model/model';
import { SharedModule } from 'src/app/shared/shared.module';
import { Categories, Printers } from 'tilby-models';
import { CategoriesFe, filterableCategoriesColumnsWithSearchBar } from '../categories.models';
import { TranslateService } from '@ngx-translate/core';
import { AlertDialogService, ConfirmDialogService, GridDownloadDialogService } from 'src/app/dialogs';
import { RowClickedEvent } from 'ag-grid-community';
import { GridSavedState, GridStateService } from 'src/app/features/items/services/grid-state.service';
import { OnDestroyService } from 'src/app/core/services/on-destroy.service';
import { subscribeInComponent } from "src/app/shared/angular-utils";
import { CliccableActionItems, GridClickableButtonItemsComponent } from 'src/app/shared/components/grid/grid-clickable-button-items/grid-clickable-button-items.component';
import { GridCellRendererService } from 'src/app/shared/components/grid/utils/grid-cell-renderer.service';
import { autobind } from 'src/app/models/decorators.model';
import { GridCellValueGetterService } from 'src/app/shared/components/grid/utils/grid-cell-value-getter.service';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { mobileCheck } from 'src/utilities';
import { ToolbarLayoutService } from 'src/app/shared/services/toolbar-layout.service';
import { GeneralInputButton } from '@tilby/tilby-ui-lib/models';

@Component({
  selector: 'app-categories-showcase',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './categories-showcase.component.html',
  styleUrl: './categories-showcase.component.scss',
  providers: [OnDestroyService] 
})
export class CategoriesShowcaseComponent implements OnInit {

  private readonly alertDialogService = inject(AlertDialogService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly entityManager = inject(EntityManagerService);
  private readonly gridCellFormatterService = inject(GridCellFormatterService);
  private readonly gridCellRendererService = inject(GridCellRendererService);
  private readonly gridDownloadDialogService = inject(GridDownloadDialogService);
  private readonly gridStateService = inject(GridStateService);  
  private readonly gridValueGetterService = inject(GridCellValueGetterService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly loaderService = inject(LoaderService);
  private readonly screenOrientationService = inject(ScreenOrientationService);
  private readonly state = inject($state);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly toolbarLayoutService = inject(ToolbarLayoutService);
  private readonly translate = inject(TranslateService);
  private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);
  
  protected isMobile = mobileCheck();
  protected isLandscape = computed(() => !!this.screenOrientationService.getOrientation().includes('landscape'));
  
  private areEditsBlocked = this.configurationManagerService.isMagoEnabled() && this.userActiveSessionManagerService.getSession()?.role !== 'root';
  private isSelected = signal(false); 

  @Input() printers: Array<Printers> = [];
  
  @ViewChild('categoriesGridComponent', {static: true}) gridRef!: GridClientSideComponent;
  protected gridPath = this.state.$current.name.split('.').pop() === 'showcase';
  query = {
      pagination: false,
      orderby_asc: "name",
  };
  value = "";
  exportFormatterOptions?: (...any: any[]) => any;
  tableData: TableData[] = [
    {
      rowData$: null,
      dataType: new CategoriesFe(),
      enableBooleanCheckboxes: true,
      booleanCheckboxesReadOnly: this.areEditsBlocked,
      headersTranslate: headersTranslate.categories_showcase,
      columnsFormatter: this.gridCellFormatterService.categories,
      cellComponentRenderer: this.gridCellRendererService.categories,
      columnsValueGetter: this.gridValueGetterService.categories
    }
  ];
  fixedButtons: GridFixedButtons = {
      checkbox: {visible: true, lockPosition: 'left'},
      customButton: {
        visible: true, 
        cellRenderer: GridClickableButtonItemsComponent, 
        lockPosition: 'right',
        cellRendererParams: {
          showEdit: true,
          showDelete: true
        }
      }
  };

  private movColumnsSwitch = false;
  private isAdvancedFilterActive = false;
  private savedState: GridSavedState | null = null;

  constructor() {
    effect(() => {
      [
        this.isLandscape(),
      ];

      setTimeout(() => {
        this.createToolbarButtons();
      }, 10)
    });

    subscribeInComponent(this.toolbarEventsService.events, (event) => {
      switch(event.type) {
        case 'toolbar.clear_search_bar':
          return this.resetSearchBar();
        case 'toolbar.search':
          return this.gridRef.onFilterTextBoxChanged(event.search, filterableCategoriesColumnsWithSearchBar);
        case 'grid.open_tool_panel':
          return this.gridRef.openToolPanel(event.panel);
        default:
          break;
      }
    });

    this.initFilters();
  }

  resetSearchBar() {
    this.toolbarEventsService.searchBarValue$.next('');
    this.toolbarEventsService.searchBarIcon$.next('tune');
    this.toolbarEventsService.searchBarIconFilled$.next('tune');
    this.toolbarEventsService.events.next({ type: 'toolbar.search', search: '' });
  }

  private initFilters() {
    this.savedState = this.gridStateService.getState("categories") || null;
    this.toolbarEventsService.searchBarValue$.next(this.savedState?.searchBarValue || '');
  }

  ngOnInit(): void {
    this.itemDataCatalogService.addDataToCatalog("printers", this.printers);

    this.toolbarEventsService.searchBarIcon$.next('tune');
    this.toolbarEventsService.searchBarIconFilled$.next('tune');
  }

  onGridReady() {
    setTimeout(async () => {
      const api = this.gridRef.gridApi;
      if (!api) return;

      // Ripristino filtri
      if(this.savedState) {
        if(this.savedState.filterModel) {
          this.gridRef.gridApi?.setFilterModel(this.savedState.filterModel);

          const model = this.savedState?.filterModel;

          for (const colId of Object.keys(model)) {
            const filterInstance = await api.getColumnFilterInstance(colId);
            
            if (filterInstance) {
              filterInstance.setModel(model[colId]);
            }
          }
        }
        
        if(this.savedState.paginationPage) {
          this.gridRef.gridApi?.paginationGoToPage(this.savedState.paginationPage);
        }
  
        this.toolbarEventsService.events.next({ type: 'toolbar.search', search: this.toolbarEventsService.searchBarValue$.value });
        api.onFilterChanged();
      }

      this.updateTable();
    })
  }

  onFilterChangedEmitter(event: any): void {
    this.saveGridState();
    this.createToolbarButtons();
  }

  saveGridState() {
    if (this.gridRef && this.gridRef.gridApi) {
      this.gridStateService.saveState("categories", {
        filterModel: this.gridRef.gridApi?.getFilterModel(),
        paginationPage: this.gridRef.gridApi?.paginationGetCurrentPage(),
        searchBarValue: this.toolbarEventsService.searchBarValue$.value
      });
    }
  }

  private getToolbarConfig() {
    const selectionActive = this.isSelected(); 
    const trueSignal = signal(true);
    
    const allButtons: GeneralInputButton[] = [
      {
        name: 'clear_filters',
        isIt: () => Object.keys(this.gridRef?.gridApi?.getFilterModel() || {}).length > 0,
        icon: signal('filter_alt_off'),
        label: signal('TOPBAR.ACTIONS.CLEAR_FILTERS'),
        click: () => this.clearGridFilters()
      },
      {
        name: 'add_new_category',
        isIt: trueSignal,
        icon: signal('playlist_add'),
        label: () => 'TOPBAR.ACTIONS.NEW_CATEGORY',
        click: () => this.state.go("app.new.items.categories.new")
      },
      {
          name: 'delete',
          isIt: () => selectionActive,
          icon: signal('delete'),
          label: signal('TOPBAR.ACTIONS.MASSIVE_DELETE'),
          click: () => this.openDeleteDialog()
      }
    ];

    const fixedButtons: GeneralInputButton[] = [
      {
        name: 'columns_panel',
        isIt: trueSignal,
        icon: signal('view_week'),
        label: signal('TOPBAR.ACTIONS.COLUMN_SELECTED'),
        click: () => this.gridRef.openToolPanel('columns')
      },
      {
        isIt: trueSignal,
        name: 'movable_columns',
        icon: signal('drag_indicator'),
        label: () => this.movColumnsSwitch ? 'TOPBAR.ACTIONS.DISABLED_COLUMN_MOVEMENT': 'TOPBAR.ACTIONS.ENABLED_COLUMN_MOVEMENT',
        click: () => this.gridRef.columnMovableSwitch(this.movColumnsSwitch = !this.movColumnsSwitch)
      },
      {
        isIt: trueSignal,
        name: 'save_columns_position',
        icon: signal('save'),
        label: signal('TOPBAR.ACTIONS.SAVE_COLUMN_PREFERENCE'),
        click: () => this.gridRef.openDialogSaveColumnsPreference()
      },
      {
        isIt: () => !this.isMobile,
        name: 'advanced_filter',
        icon: () => this.isAdvancedFilterActive ? 'filter_alt_off' : 'filter_list_alt',
        label: () => this.isAdvancedFilterActive ? 'TOPBAR.ACTIONS.ADVANCED_FILTERS_DISABLED' : 'TOPBAR.ACTIONS.ADVANCED_FILTERS_ENABLED',
        click: () => this.gridRef.advancedFilterSwitch(this.isAdvancedFilterActive = !this.isAdvancedFilterActive)
      },
      {
        isIt: trueSignal,
        name: 'upload',
        icon: signal('upload'),
        label: signal('TOPBAR.ACTIONS.IMPORT'),
        click: () => this.goToImporter()
      },
      {
        isIt: trueSignal,
        name: 'update',
        icon: signal('update'),
        label: signal('ITEMS.TOPBAR_ITEMS.UPDATE'),
        click: () => this.updateTable()
      },
      {
        name: 'export',
        isIt: () => true,
        icon: signal('download'),
        label: signal('TOPBAR.ACTIONS.EXPORT'),
        click: () => this.triggerExport()
      },
      {
        isIt: trueSignal,
        name: 'settings_backup_restore',
        icon: signal('settings_backup_restore'),
        label: signal('TOPBAR.ACTIONS.RESET_TABLE'),
        click: () => this.gridRef.resetLayout()
      }
    ];

    return { allButtons, fixedButtons };
  }

  createToolbarButtons() {
    const { allButtons, fixedButtons } = this.getToolbarConfig();
    this.toolbarLayoutService.updateToolbarLayout(allButtons, fixedButtons);
  }

  clearGridFilters() {
    setTimeout(async () => {
      const api = this.gridRef.gridApi;
      if (!api) return;

      const model = this.gridStateService.getState("categories")?.filterModel;

      if(model) {
        for (const colId of Object.keys(model)) {
          const filterInstance = await api.getColumnFilterInstance(colId);
          
          if (filterInstance) {
            filterInstance.setModel(null);
          }
        }
      }

      api.onFilterChanged();
    })
  }

  @autobind
  onExport(dataExport: DataExport) {
    // this.gridRef?.onBtnExport(dataExport, "item_categories");
    const rowSelected = !!this.gridRef.getSelectedCount();
    this.gridRef?.clientSideExport(dataExport, rowSelected);
  }

  goToImporter() {
    this.state.go('app.new.file_importer');
  }

  async triggerExport() {
    const res = await this.gridDownloadDialogService.openDialog('categories', {
      data: this.tableData.map(tableData => ({
        ...tableData,
        sortFilterQuery: this.gridRef.getSortFilterQuery(),
        countSelectedRows: this.gridRef.gridApi?.getSelectedRows().length
      }))
    }, "item_categories");

    if (res) {
      this.onExport(res);
    }
  }

  updateTable() {
    this.tableData[0].rowData$ = of(this.itemDataCatalogService.getDataByKey('categories') || []).pipe(delay(100));
  }

  rowClicked({data}: RowClickedEvent) {
    // this.movColumnsSwitch && this.gridRef.columnMovableSwitch(this.movColumnsSwitch = false);
    if (this.state.router.globals.transition) {
      return; 
    }
    this.goToDetails(data);
  }

  actions({ action, data }: CliccableActionItems<Categories>) {
    if(data) {
      switch (action) {
        case "details": 
          return this.goToDetails(data);
        case "delete": 
          return this.openDeleteDialog(data.id);
      }
    }
  }

  goToDetails(row: Categories) {
    if(!row || !row.id) return;
    this.state.go("app.new.items.categories.details", {categoryId: row.id });
    this.loaderService.show();
  }

  async openDeleteDialog(id?: number, askConfirm = true) {
    if(this.areEditsBlocked) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.NO_EDIT_WITH_MAGO_ENABLED')
        }})
      return;
    }

    let categoriesToDelete: any[] = [];

    if(id) {
      categoriesToDelete = [{ id: id }];
    } else {
      categoriesToDelete = this.gridRef.gridApi?.getSelectedRows() || [];
    }

    if(categoriesToDelete.length === 0) return;

    if(askConfirm) {
      let message = this.translate.instant('ITEMS.SHOWCASE.WANT_TO_DELETE_TITLE');
      const confirm = await this.confirmDialogService.openDialog({
          data: {
              messageLabel: message
          }
      });
  
      if(!confirm) return;
    }

    this.gridRef.onRemoveSelected(id);
  }

  deleteCategories(ids: number[]) {
    const deletePromises = ids.map(id => this.entityManager.categories.deleteOneOnline(id));

    Promise.all(deletePromises)
    .then((results) => {

      // Aggiornamento lista categorie
      // const currentCategories = this.itemDataCatalogService.getDataByKey('categories') || [];

      // const idSet = new Set(ids);

      // const updatedList = currentCategories.filter(c => !idSet.has(c.id || -1));

      // this.itemDataCatalogService.addDataToCatalog('categories', updatedList);
      // this.itemDataCatalogService.updateFilteredDataToCatalog('categories', updatedList);

      // Ricarica i dati della griglia
      // this.tableData[0].rowData$ = of(updatedList);
      this.tableData[0].rowData$ = of(this.itemDataCatalogService.getDataByKey("categories") || []);
      
      // Pulizia selezione visiva
      this.gridRef.gridApi?.deselectAll();
      this.isSelected.set(false);
    })
    .catch((error) => {
      console.error("Errore durante la cancellazione della categoria: ", error);
    })
  }

  handleSelectionChange(selected: boolean) {
    this.isSelected.set(selected);
    this.createToolbarButtons(); 
  }
}